import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { AlertTriangle, ExternalLink, XCircle, Phone, RefreshCw, Undo2, Eye, ChevronDown, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import EmailPreviewDialog from "./EmailPreviewDialog";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

export default function BouncedEmailsWidget() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dismissingId, setDismissingId] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  // Collapsed by default — a tiny chip shows the count and expands the full
  // widget on tap. Keeps the Dashboard uncluttered on days with 1-3
  // stragglers but still puts the number one glance away.
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${API_URL}/emails/bounced?limit=25`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmails(data.emails || []);
    } catch (err) {
      console.error("Failed to load bounced emails:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dismiss = async (id) => {
    setDismissingId(id);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/emails/${id}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Optimistic remove
      setEmails((prev) => prev.filter((e) => e.id !== id));
      toast.success("Dismissed", {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await axios.post(`${API_URL}/emails/${id}/undismiss`, {}, {
                headers: { Authorization: `Bearer ${token}` },
              });
              load(true);
              toast.success("Restored");
            } catch { toast.error("Couldn't restore"); }
          },
        },
      });
    } catch {
      toast.error("Couldn't dismiss");
    } finally {
      setDismissingId(null);
    }
  };

  const dismissAll = async () => {
    if (emails.length === 0) return;
    if (!window.confirm(`Dismiss all ${emails.length} bounced email${emails.length === 1 ? "" : "s"}? You can restore them from the Email Log.`)) return;
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${API_URL}/emails/bounced/dismiss-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEmails([]);
      toast.success(`Dismissed ${data.dismissed || 0}`);
    } catch {
      toast.error("Couldn't dismiss all");
    }
  };

  // Empty state — hidden entirely (returns null) so the dashboard stays clean
  if (!loading && emails.length === 0) return null;

  // Collapsed chip — the default state. Sits at the top of the Dashboard
  // as a small amber pill instead of a full card so the day's bounces are
  // one glance / one tap away without hogging vertical space.
  if (!expanded) {
    if (loading) {
      // Silent while loading so the chip doesn't flash-in.
      return null;
    }
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="inline-flex items-center gap-2 h-9 px-3 rounded-full bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-800 text-sm font-medium transition-colors"
        data-testid="bounced-emails-chip"
        title="Show bounced / failed emails"
      >
        <AlertTriangle className="w-4 h-4" />
        <span>
          {emails.length} bounced email{emails.length === 1 ? "" : "s"}
        </span>
        <ChevronDown className="w-3.5 h-3.5 opacity-60" />
      </button>
    );
  }

  return (
    <Card className="shadow-sm border-rose-200 bg-rose-50/30" data-testid="bounced-emails-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2 text-rose-800">
            <AlertTriangle className="w-4 h-4" />
            Bounced / Failed Emails
            {emails.length > 0 && (
              <Badge className="bg-rose-500 hover:bg-rose-500 text-white text-[10px] font-bold ml-1" data-testid="bounce-count-badge">
                {emails.length}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => load(true)}
              disabled={refreshing}
              className="h-7 text-xs"
              data-testid="refresh-bounces-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={dismissAll}
              disabled={emails.length === 0}
              className="h-7 text-xs text-rose-700 hover:bg-rose-100"
              data-testid="dismiss-all-bounces-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              Dismiss all
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(false)}
              className="h-7 w-7 p-0 text-rose-700 hover:bg-rose-100"
              data-testid="collapse-bounces-btn"
              aria-label="Collapse bounced emails"
              title="Collapse"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <p className="text-xs text-rose-700/70 mt-1">Bad addresses caught by Resend. Fix them in the contact record so future automations land.</p>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="text-center py-6 text-sm text-slate-400" data-testid="bounces-loading">Loading…</div>
        ) : (
          <div className="divide-y divide-rose-100 border border-rose-100 rounded-lg overflow-hidden bg-white" data-testid="bounces-list">
            {emails.map((e) => {
              const statusColor = e.status === "failed" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-rose-50 text-rose-700 border-rose-200";
              const StatusIcon = e.status === "failed" ? XCircle : AlertTriangle;
              return (
                <div key={e.id} className="px-3.5 py-3 hover:bg-rose-50/40 transition" data-testid={`bounce-row-${e.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`text-[11px] font-semibold border ${statusColor} flex items-center gap-1`}>
                          <StatusIcon className="w-3 h-3" />
                          {e.status === "failed" ? "Failed" : e.status === "complained" ? "Complaint" : "Bounced"}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-200 bg-slate-50">{e.category_label || "Email"}</Badge>
                        <span className="text-[11px] text-slate-400">{fmtDate(e.created_at)}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-800 mt-1.5">
                        <span className="text-rose-700">{e.to}</span>
                        {e.contact_name && <span className="text-slate-500"> · {e.contact_name}</span>}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        {(e.subject || "(no subject)")}
                        {e.project_name && <span className="text-slate-400"> · {e.project_name}</span>}
                      </p>
                      {e.status_detail && (
                        <p className="text-[11px] text-rose-600 mt-1 italic">{e.status_detail}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => setPreviewId(e.id)}
                        data-testid={`bounce-preview-${e.id}`}
                        title="Preview the email that bounced"
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" />Preview
                      </Button>
                      {e.contact_phone && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => window.location.href = `tel:${e.contact_phone}`}
                          data-testid={`bounce-call-${e.id}`}
                          title={`Call ${e.contact_phone}`}
                        >
                          <Phone className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {e.contact_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => navigate(`/contacts/${e.contact_id}`)}
                          data-testid={`bounce-fix-${e.id}`}
                        >
                          Fix email
                        </Button>
                      )}
                      {e.project_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs text-slate-500"
                          onClick={() => navigate(`/projects/${e.project_id}`)}
                          data-testid={`bounce-open-project-${e.id}`}
                          title="Open project"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-11 w-11 sm:h-8 sm:w-8 p-0 text-slate-400 hover:text-rose-700 hover:bg-rose-50"
                        onClick={() => dismiss(e.id)}
                        disabled={dismissingId === e.id}
                        data-testid={`bounce-dismiss-${e.id}`}
                        title="Dismiss this bounce"
                        aria-label="Dismiss"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      <EmailPreviewDialog
        open={!!previewId}
        onOpenChange={(open) => !open && setPreviewId(null)}
        recordId={previewId}
      />
    </Card>
  );
}
